import { createSlice } from "@reduxjs/toolkit";
import { act } from "react";
import { data } from "react-router-dom";

const initialState = {
  loading: false,
  error: null,
  clientsubmissions: {
    data: [],
    total: 0,
    loading: false,
  },
  confirmedOrStart: {
    data: [],
    total: 0,
    loading: false,
  },
  followupInterviews: {
    data: [],
    total: 0,
    loading: false,
  },
  allJobs: {
    data: [],
  },
  pipeline: {
    data: [],
    loading: false,
  },
  jobBoards: {
    internalDb: {
      recentHistory: { data: [] },
      savedHistory: { data: [] },
    },
    careerBuilder: {
      recentHistory: { data: [] },
      savedHistory: { data: [] },
    },
    monster: {
      classic: {
        data: [],
        total: 0,
        tableResponse: null,
        fullResponse: null,
        existingCandidates: null,
        recentHistory: { data: [] },
        savedHistory: { data: [] },
      },
      power: {
        data: [],
        recentHistory: { data: [] },
        savedHistory: { data: [] },
      },
      monster: {
        data: [],
        recentHistory: { data: [] },
        savedHistory: { data: [] },
      },
      search: {
        data: [],
        recentHistory: { data: [] },
        savedHistory: { data: [] },
      },
    },
    kultSearch: {
      recentHistory: { data: [] },
      savedHistory: { data: [] },
    },
    integratedSearch: {
      recentHistory: { data: [] },
      savedHistory: { data: [] },
    },
  },
  resumeMining: {
    data: [],
    loading: false,
  },
  resumeData: {
    data: [],
  },
  expiringJobsDisplayedOn: null,
  companyLeads: {
    data: [],
    total: 0,
    loading: false,
  },
  jobLeads: {
    data: [],
    total: 0,
    loading: false,
  },
};

const bdmJobsSlice = createSlice({
  name: "bdmjobs",
  initialState,
  reducers: {
    setBdmStateLoading(state) {
      state.loading = true;
    },
    revertBdmStateLoading(state) {
      state.loading = false;
    },
    // Start fetching submissions
    fetchClientSubmissionsStart(state) {
      state.clientsubmissions.loading = true;
      state.error = null;
    },
    // Success response
    fetchClientSubmissionsSuccess(state, action) {
      state.clientsubmissions.loading = false;
      state.clientsubmissions.data = action.payload.data || [];
      state.clientsubmissions.total =
        action.payload.total || action.payload.data?.length || 0;
    },
    // Failure response
    fetchClientSubmissionsFailure(state, action) {
      state.clientsubmissions.loading = false;
      state.error = action.payload;
    },
    // Clear/reset submissions
    clearClientSubmissions(state) {
      state.clientsubmissions = { data: [], total: 0, loading: false };
      state.clientsubmissions.loading = false;
      state.error = null;
    },
    // Update state
    updateJobNotes: (state, action) => {
      const { jobPostIds, newNote } = action.payload;

      if (!Array.isArray(state.allJobs.data)) return;

      state.allJobs.data = state.allJobs.data.map((job) => {
        if (jobPostIds.includes(job.uiid)) {
          // Ensure job.notes is always an array
          const existingNotes = Array.isArray(job.notes) ? job.notes : [];

          return {
            ...job,
            notes: [
              ...existingNotes,
              {
                ...newNote,
                uiid: newNote.uiid || Date.now(), // temporary ID
                addedOn: newNote.addedOn || new Date().toISOString(),
              },
            ],
          };
        }
        return job;
      });
    },

    // -------- ALL JOBS --------
    fetchAllJobsStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchAllJobsSuccess(state, action) {
      state.loading = false;
      state.allJobs.data = action.payload.data || [];
    },
    fetchAllJobsFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    clearAllJobs(state) {
      state.allJobs = { data: [] };
      state.loading = false;
      state.error = null;
    },
    updateRecruiter: (state, action) => {
      const { jobUiid, notifyTo } = action.payload;
      if (!Array.isArray(state.allJobs.data)) return;

      const job = state.allJobs.data.find((job) => job.uiid === jobUiid);
      if (job) {
        job.notifyTo = notifyTo;
      }
    },
    updateBdmJobs: (state, action) => {
      state.allJobs.data = action.payload;
    },

    // -------- PIPELINE --------
    fetchPipelineStart(state) {
      state.pipeline.loading = true;
      state.error = null;
    },
    fetchPipelineSuccess(state, action) {
      state.pipeline.loading = false;
      state.pipeline.data = action.payload.data || [];
    },
    fetchPipelineFailure(state, action) {
      state.pipeline.loading = false;
      state.error = action.payload;
    },
    clearPipeline(state) {
      state.pipeline = { data: [], loading: false };
      state.pipeline.loading = false;
      state.error = null;
    },
    // -------- MONSTER CLASSIC SEARCH --------
    fetchMonsterClassicStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchMonsterClassicSuccess(state, action) {
      state.loading = false;
      state.error = false;
      state.jobBoards.monster.classic.data = action.payload.data || [];
      state.jobBoards.monster.classic.total = action.payload.total;
      state.jobBoards.monster.classic.tableResponse =
        action.payload.tableResponse;
      state.jobBoards.monster.classic.fullResponse =
        action.payload.fullResponse;
      state.jobBoards.monster.classic.existingCandidates =
        action.payload.existingCandidates;
    },
    fetchMonsterClassicFailure(state, action) {
      state.loading = false;
      state.error = action.payload || "Failed to fetch Monster Classic Search";
    },
    clearMonsterClassic(state) {
      state.jobBoards.monster.classic = { data: [] };
      state.loading = false;
      state.error = null;
    },
    //  Recent History
    fetchRecentHistoryStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchRecentHistorySuccess(state, action) {
      const { board, subBoard, data } = action.payload;
      state.loading = false;

      if (board === "monster" && subBoard) {
        state.jobBoards.monster[subBoard].recentHistory.data = data || [];
      } else if (state.jobBoards[board]) {
        state.jobBoards[board].recentHistory.data = data || [];
      }
    },

    fetchRecentHistoryFailure(state, action) {
      state.loading = false;
      state.error = action.payload || "Failed to fetch recent history";
    },
    clearRecentHistory(state, action) {
      const board = action.payload;
      if (state.jobBoards[board]) {
        state.jobBoards[board].recentHistory.data = [];
      }
    },

    //  Saved History
    fetchSavedHistoryStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchSavedHistorySuccess(state, action) {
      const { board, subBoard, data } = action.payload;
      state.loading = false;

      if (board === "monster" && subBoard) {
        state.jobBoards.monster[subBoard].savedHistory.data = data || [];
      } else if (state.jobBoards[board]) {
        state.jobBoards[board].savedHistory.data = data || [];
      }
    },
    fetchSavedHistoryFailure(state, action) {
      state.loading = false;
      state.error = action.payload || "Failed to fetch saved history";
    },
    clearSavedHistory(state, action) {
      const board = action.payload;
      if (state.jobBoards[board]) {
        state.jobBoards[board].savedHistory.data = [];
      }
    },
    // CONFIRMED OR START
    fetchConfirmedOrStartStart(state) {
      state.confirmedOrStart.loading = true;
      state.error = null;
    },

    fetchConfirmedOrStartSuccess(state, action) {
      state.confirmedOrStart.loading = false;
      state.confirmedOrStart.data = action.payload.data || [];
      state.confirmedOrStart.total =
        action.payload.total || action.payload.data?.length || 0;
    },

    fetchConfirmedOrStartFailure(state, action) {
      state.confirmedOrStart.loading = false;
      state.error = action.payload || "Failed to fetch Confirmed or Start data";
    },

    clearConfirmedOrStart(state) {
      state.confirmedOrStart = { data: [], total: 0, loading: false };
      state.confirmedOrStart.loading = false;
      state.error = null;
    },
    //  FOLLOW-UP INTERVIEWS
    fetchFollowupInterviewsStart(state) {
      state.followupInterviews.loading = true;
      state.error = null;
    },

    fetchFollowupInterviewsSuccess(state, action) {
      state.followupInterviews.loading = false;
      state.followupInterviews.data = action.payload.data || [];
      state.followupInterviews.total =
        action.payload.total || action.payload.data?.length || 0;
    },

    fetchFollowupInterviewsFailure(state, action) {
      state.followupInterviews.loading = false;
      state.error = action.payload || "Failed to fetch Follow-up Interviews";
    },

    clearFollowupInterviews(state) {
      state.followupInterviews = { data: [], total: 0, loading: false };
      state.followupInterviews.loading = false;
      state.error = null;
    },

    // Resume Mining
    fetchResumeMiningStart(state) {
      state.resumeMining.data = [];
      state.resumeMining.loading = true;
      state.error = null;
    },
    fetchResumeMiningSuccess(state, action) {
      state.resumeMining.loading = false;
      state.resumeMining.data = action.payload.data || [];
    },
    fetchResumeMiningFailure(state, action) {
      state.resumeMining.loading = false;
      state.error = action.payload;
    },
    clearResumeMining(state) {
      state.resumeMining.data = [];
      state.resumeMining.loading = false;
      state.error = null;
    },
    fetchResumeDataStart(state) {
      state.loading = false;
    },
    resumeDataSuccess(state, action) {
      state.resumeData.data = action.payload.data;
    },
    resumeDataFailure(state) {
      state.error = "localStorage error";
    },
    setExpiringJobsDisplayedOn(state, action) {
      console.log("action: ", action);
      state.expiringJobsDisplayedOn = action.payload;
    },
    updateAllOtherSourceNotes: (state, action) => {
      const { applicantId, newNoteUiid, note, addedBy, addedOn, uiid } =
        action.payload || {};
      const applicantLists = [
        state.pipeline?.data,
        state.clientsubmissions?.data,
        state.confirmedOrStart?.data,
        state.followupInterviews?.data,
      ];
      applicantLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((applicant) => {
          if (String(applicant?.candidateId?.uiid) !== String(applicantId))
            return;
          applicant.notes ??= [];
          applicant.notesList ??= [];
          const noteExists = applicant.notes.some(
            (id) => String(id) === String(newNoteUiid),
          );
          if (!noteExists) {
            applicant.notes.push(newNoteUiid);
          }
          const exists = applicant.notesList.some(
            (n) => String(n.uiid) === String(uiid),
          );
          if (!exists) {
            applicant.notesList.unshift({
              uiid,
              note,
              addedBy,
              addedOn,
            });
            applicant.notesList.sort(
              (a, b) => new Date(b.addedOn) - new Date(a.addedOn),
            );
          }
        });
      });
    },
    // FOR LEAD COMPANIES IN BDM
    fetchCompanyLeadsStart(state) {
      state.companyLeads.loading = true;
      state.error = null;
    },
    fetchCompanyLeadsSuccess(state, action) {
      state.companyLeads.loading = false;
      state.companyLeads.data = action.payload.data || [];
      state.companyLeads.total =
        action.payload.total || action.payload.data?.length || 0;
    },
    fetchCompanyLeadsFailure(state, action) {
      state.companyLeads.loading = false;
      state.error = action.payload;
    },
    clearCompanyLeads(state) {
      state.companyLeads = { data: [], total: 0, loading: false };
      state.error = null;
    },
    removeCompanyFromState(state, action) {
      const deletedUiid = action.payload;
      state.companyLeads.data = state.companyLeads.data.filter(
        (company) => company.uiid !== deletedUiid,
      );
      state.companyLeads.total =
        state.companyLeads.total > 0 ? state.companyLeads.total - 1 : 0;
    },
    // FOR LEAD JOBS
    fetchJobLeadsStart(state) {
      state.jobLeads.loading = true;
      state.error = null;
    },

    fetchJobLeadsSuccess(state, action) {
      state.jobLeads.loading = false;
      state.jobLeads.data = action.payload.data || [];
      state.jobLeads.total =
        action.payload.total || action.payload.data?.length || 0;
    },

    fetchJobLeadsFailure(state, action) {
      state.jobLeads.loading = false;
      state.error = action.payload;
    },
    updateApplicantNotesListBDM(state, action) {
      const { applicantId, notesList } = action.payload;
      const applicantLists = [
        state.pipeline?.data,
        state.confirmedOrStart?.data,
        state.followupInterviews?.data,
        state.clientsubmissions?.data,
      ];
      applicantLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        const applicant = list.find(
          (a) => String(a?.candidateId?.uiid) === String(applicantId),
        );
        if (!applicant) return;
        const existingIds = Array.isArray(applicant.notes)
          ? applicant.notes
          : [];
        const safeNotes = Array.isArray(notesList) ? notesList : [];
        const noteIds = safeNotes
          .map((n) => (typeof n === "object" ? n?.uiid : n))
          .filter(Boolean);
        applicant.notes = [...new Set([...existingIds, ...noteIds])];
        // applicant.notesList = Array.isArray(notesList) ? notesList : [];
         const existingNotes = Array.isArray(applicant.notesList) ? applicant.notesList : [];
        const merged = [...existingNotes, ...safeNotes];
        applicant.notesList = Array.from(new Map(merged.map(n => [String(n.uiid), n])).values())
          .sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn));
      });
    },
    addNoteToApplicantBDM(state, action) {
      const { applicantId, note, addedBy, addedOn, uiid } =
        action.payload || {};

      const applicantLists = [
        state.pipeline?.data,
        state.confirmedOrStart?.data,
        state.followupInterviews?.data,
        state.clientsubmissions?.data,
      ];
      applicantLists.forEach((list) => {
        if (!Array.isArray(list)) return;

        const applicant = list.find(
          (a) => String(a?.candidateId?.uiid) === String(applicantId),
        );

        if (!applicant) return;

        applicant.notes ??= [];
        applicant.notesList ??= [];

        const exists = applicant.notesList.some(
          (n) => String(n?.uiid) === String(uiid),
        );

        if (!exists) {
          applicant.notesList.unshift({
            uiid,
            note,
            addedBy,
            addedOn,
          });

          const noteExists = applicant.notes.some(
            (id) => String(id) === String(uiid),
          );
          if (!noteExists) {
            applicant.notes.push(uiid);
          }
          applicant.notesList.sort(
            (a, b) => new Date(b.addedOn) - new Date(a.addedOn),
          );
        }
      });
    },
  },
});

export const {
  setBdmStateLoading,
  revertBdmStateLoading,
  fetchClientSubmissionsStart,
  fetchClientSubmissionsSuccess,
  fetchClientSubmissionsFailure,
  clearClientSubmissions,
  fetchAllJobsStart,
  fetchAllJobsSuccess,
  fetchAllJobsFailure,
  clearAllJobs,
  fetchPipelineStart,
  fetchPipelineSuccess,
  fetchPipelineFailure,
  clearPipeline,
  fetchMonsterClassicStart,
  fetchMonsterClassicSuccess,
  fetchMonsterClassicFailure,
  clearMonsterClassic,
  fetchRecentHistoryStart,
  fetchRecentHistorySuccess,
  fetchRecentHistoryFailure,
  clearRecentHistory,
  fetchSavedHistoryStart,
  fetchSavedHistorySuccess,
  fetchSavedHistoryFailure,
  clearSavedHistory,
  fetchConfirmedOrStartStart,
  fetchConfirmedOrStartSuccess,
  fetchConfirmedOrStartFailure,
  clearConfirmedOrStart,
  fetchFollowupInterviewsStart,
  fetchFollowupInterviewsSuccess,
  fetchFollowupInterviewsFailure,
  clearFollowupInterviews,
  updateJobNotes,
  fetchResumeMiningStart,
  fetchResumeMiningSuccess,
  fetchResumeMiningFailure,
  clearResumeMining,
  updateRecruiter,
  fetchResumeDataStart,
  resumeDataSuccess,
  resumeDataFailure,
  setExpiringJobsDisplayedOn,
  updateBdmJobs,
  updateAllOtherSourceNotes,
  fetchCompanyLeadsStart,
  fetchCompanyLeadsSuccess,
  fetchCompanyLeadsFailure,
  clearCompanyLeads,
  removeCompanyFromState,
  fetchJobLeadsStart,
  fetchJobLeadsSuccess,
  fetchJobLeadsFailure,
  updateBdmApplicantNotesList,
  updateApplicantNotesListBDM,
  addNoteToApplicantBDM,
} = bdmJobsSlice.actions;

export default bdmJobsSlice.reducer;
